import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { useAuth } from '@/hooks/use-auth';
import { productService } from '@/lib/packages';
import { ProductRow } from '@/integrations/supabase/types';
import { Loader2, Package, Search } from 'lucide-react';

interface AddProductModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onProductAdd: (productId: string, notes?: string) => void;
  isAdding: boolean;
}

export const AddProductModal = ({ 
  open, 
  onOpenChange, 
  onProductAdd, 
  isAdding 
}: AddProductModalProps) => {
  const { companyId } = useAuth();
  const [selectedProduct, setSelectedProduct] = useState<ProductRow | null>(null);
  const [productSearch, setProductSearch] = useState('');
  const [products, setProducts] = useState<ProductRow[]>([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [showProductResults, setShowProductResults] = useState(false);
  const [notes, setNotes] = useState('');

  // Search products
  useEffect(() => {
    if (productSearch.length >= 2 && companyId) {
      setLoadingProducts(true);
      productService.searchProducts(companyId, productSearch, 20).then((results) => {
        setProducts(results);
        setLoadingProducts(false);
        setShowProductResults(true);
      }).catch(() => {
        setLoadingProducts(false);
        setShowProductResults(false);
      });
    } else {
      setShowProductResults(false);
      setProducts([]);
    }
  }, [productSearch, companyId]);

  const handleProductSelect = (product: ProductRow) => {
    setSelectedProduct(product);
    setProductSearch(product.name);
    setShowProductResults(false);
  };

  const handleSubmit = () => {
    if (selectedProduct) {
      onProductAdd(selectedProduct.id, notes);
      // Reset form
      setSelectedProduct(null);
      setProductSearch('');
      setNotes('');
      onOpenChange(false);
    }
  };

  const resetForm = () => {
    setSelectedProduct(null);
    setProductSearch('');
    setNotes('');
    setShowProductResults(false);
  };

  useEffect(() => {
    if (!open) {
      resetForm();
    }
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Ziyarete Ürün Ekle</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Product Search */}
          <div className="space-y-2">
            <Label>Ürün Ara</Label>
            <div className="relative">
              <Search className="absolute left-3 top-3 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Ürün adı veya kodu ara..."
                value={productSearch}
                onChange={(e) => setProductSearch(e.target.value)}
                className="pl-10"
                onFocus={() => {
                  if (productSearch.length >= 2 && products.length > 0) {
                    setShowProductResults(true);
                  }
                }}
              />
              
              {showProductResults && (
                <div className="absolute top-full mt-1 w-full bg-popover border border-border rounded-md shadow-lg z-10 max-h-48 overflow-y-auto backdrop-blur-sm">
                  {loadingProducts ? (
                    <div className="p-3 text-muted-foreground text-center">
                      <Loader2 className="w-4 h-4 animate-spin inline-block mr-2" />
                      Ürünler aranıyor...
                    </div>
                  ) : products.length > 0 ? (
                    products.map((product) => (
                      <div
                        key={product.id}
                        className="flex items-center gap-3 p-3 hover:bg-accent cursor-pointer"
                        onClick={() => handleProductSelect(product)}
                      >
                        <Package className="w-8 h-8 text-muted-foreground" />
                        <div>
                          <p className="font-medium">{product.name}</p>
                          <p className="text-sm text-muted-foreground">
                            {product.item_code} • ₺{product.price.toLocaleString('tr-TR')}
                          </p>
                        </div>
                      </div>
                    ))
                  ) : productSearch.length >= 2 ? (
                    <div className="p-3 text-muted-foreground text-center">
                      Ürün bulunamadı
                    </div>
                  ) : (
                    <div className="p-3 text-muted-foreground text-center">
                      En az 2 karakter girin
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Selected Product */}
          {selectedProduct && (
            <Card className="bg-accent/5">
              <CardContent className="p-3">
                <div className="flex items-center gap-3">
                  <Package className="w-8 h-8 text-accent" />
                  <div>
                    <p className="font-medium">{selectedProduct.name}</p>
                    <p className="text-sm text-muted-foreground">
                      {selectedProduct.item_code} • ₺{selectedProduct.price.toLocaleString('tr-TR')}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Notes */}
          <div className="space-y-2">
            <Label>Notlar (İsteğe bağlı)</Label>
            <Textarea
              placeholder="Bu ürünle ilgili notlar..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
            />
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <Button variant="outline" onClick={() => onOpenChange(false)} className="flex-1">
              İptal
            </Button>
            <Button 
              onClick={handleSubmit} 
              disabled={!selectedProduct || isAdding}
              className="flex-1"
            >
              {isAdding ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Ekleniyor...
                </>
              ) : (
                'Ürün Ekle'
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
